import React, { useMemo } from 'react';
import { ConfigProvider, Menu as AntMenu, Dropdown as AntDropdown, DropDownProps } from 'antd';
import { MenuProps, MenuItemProps, MenuDividerProps } from 'antd/lib/menu';
import { DropdownButtonProps } from 'antd/lib/dropdown';
import styled, { useTheme } from 'styled-components';

import { buildNextDropdownTheme, buildNextMenuTheme } from '../next/tokens';

export type { MenuProps } from 'antd/lib/menu';
export type { MenuItemProps } from 'antd/lib/menu';
export type { DropDownProps } from 'antd/lib/dropdown';

type DropdownVariant = 'next';

interface DropdownWithVariantProps extends DropDownProps {
  variant?: DropdownVariant;
}

export const Dropdown: React.FC<DropdownWithVariantProps> = ({ variant, ...props }) => {
  const { token } = useTheme();
  const nextDropdownTheme = useMemo(() => buildNextDropdownTheme(token.next), [token.next]);
  if (variant === 'next') {
    const overlayClassName = ['ant-dropdown-next', props.overlayClassName]
      .filter(Boolean)
      .join(' ');

    return (
      <ConfigProvider theme={nextDropdownTheme}>
        <AntDropdown {...props} overlayClassName={overlayClassName} />
      </ConfigProvider>
    );
  }
  return <AntDropdown {...props} />;
};

type MenuVariant = 'next';

interface MenuWithVariantProps extends MenuProps {
  variant?: MenuVariant;
}

/**
 * Minimal CSS for Menu next variant — only for things ConfigProvider tokens can't express.
 */
const NextMenu = styled(AntMenu)`
  &.ant-menu {
    border: none !important;
    background: transparent;

    .ant-menu-submenu-selected {
      > .ant-menu-submenu-title[aria-expanded='false'] {
        background-color: ${({ theme }) => theme.token.next.palette.blue50};
      }
      > .ant-menu-submenu-title[aria-expanded='true'] {
        color: ${({ theme }) => theme.token.next.palette.grey800};
      }
    }

    // Sets padding for different level of nested menus.
    // We can use inlineIndent={24}. It would give 24, 48, 72, 96, etc. but we want 8, 32, 56, 80, etc.
    // So we do it manually. We are supporting up to 3 levels right now.
    .ant-menu-item,
    .ant-menu-submenu-title {
      padding-inline-end: 8px !important;
    }

    /* L1 */
    > .ant-menu-item,
    > .ant-menu-submenu > .ant-menu-submenu-title {
      padding-inline-start: 8px !important;
    }

    /* L2 */
    > .ant-menu-submenu > .ant-menu-sub {
      > .ant-menu-item,
      > .ant-menu-submenu > .ant-menu-submenu-title {
        padding-inline-start: 32px !important;
      }

      /* L3 */
      > .ant-menu-submenu > .ant-menu-sub {
        > .ant-menu-item,
        > .ant-menu-submenu > .ant-menu-submenu-title {
          padding-inline-start: 56px !important;
        }
      }
    }

    // Support for root level item groups.
    > .ant-menu-item-group > .ant-menu-item-group-title {
      padding-inline: 8px !important;
    }
  }
` as typeof AntMenu;

export const Menu: React.FC<MenuWithVariantProps> = ({ variant, ...rest }) => {
  const { token } = useTheme();
  const nextMenuTheme = useMemo(() => buildNextMenuTheme(token.next), [token.next]);
  if (variant === 'next') {
    return (
      <ConfigProvider theme={nextMenuTheme}>
        <NextMenu {...rest} />
      </ConfigProvider>
    );
  }
  return <AntMenu {...rest} />;
};

export const MenuItem: React.FC<MenuItemProps> = (props) => <AntMenu.Item {...props} />;

export const DropdownButton: React.FC<Omit<DropdownButtonProps, 'arrow'>> = ({
  size = 'large',
  ...rest
}) => <AntDropdown.Button size={size} {...rest} />;

export const MenuDivider: React.FC<MenuDividerProps> = (props) => <AntMenu.Divider {...props} />;
